import type { Api } from '@limuzin/kit';
import type {
  ConfigView, MeView, MessageView, PassengerRideView, PlaceView, ProofStateView,
  SavedPlaceView, SessionView, StartedProofView, SupportMessageView, SupportThreadView,
  TownTariffView, TownView,
} from '@limuzin/passenger';

/**
 * Every call this app makes, in one file, named for what the passenger is doing
 * rather than for the HTTP verb — the same arrangement as
 * `apps/rider/lib/data/rider_repository.dart`, which is the same set of calls.
 *
 * The types come from `server/src/rides/passenger.contract.ts`: the server's own
 * description of what it sends. A field renamed there is a compile error here.
 *
 * Nothing below interprets a failure. Every error the API returns carries a Russian
 * `message` the server wrote for the person who caused it, and the screens show it
 * verbatim rather than translating a status code into a guess.
 */

// ------------------------------------------------------------------ before signing in

const SUPPORT_PHONE_KEY = 'limuzin.customer.support-phone';
const ORDER_PHONE_KEY = 'limuzin.customer.order-phone';

function cached(key: string): string | null {
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

/** Last support number offered by the server, for a cancel it never heard. */
export const cachedSupportPhone = () => cached(SUPPORT_PHONE_KEY);

/**
 * Last order number offered by the server for the passenger's town, for ordering
 * while it is unreachable.
 */
export const cachedOrderPhone = () => cached(ORDER_PHONE_KEY);

/**
 * The service's numbers, each kept for a visit without a connection. The order line
 * is a town's: asked without [townId], as before sign-in, there is none, and the one
 * kept from the town last asked about stays as it was.
 */
export async function getConfig(api: Api, townId?: string | null): Promise<ConfigView> {
  const config = await api.get<ConfigView>(
    townId ? `/api/config?town=${encodeURIComponent(townId)}` : '/api/config',
  );
  const kept: (readonly [string, string | null])[] = [[SUPPORT_PHONE_KEY, config.supportPhone]];
  if (townId) kept.push([ORDER_PHONE_KEY, config.orderPhone]);
  for (const [key, phone] of kept) {
    try {
      if (phone) localStorage.setItem(key, phone);
      else localStorage.removeItem(key);
    } catch {
      // The online call still works; storage only decides whether it is offered offline.
    }
  }
  return config;
}

export const getTowns = (api: Api) => api.get<TownView[]>('/api/towns');

export const getTariff = (api: Api, townId: string) =>
  api.get<TownTariffView>(`/api/towns/${townId}/tariff`);

/**
 * Whether it is light outside a town, and until when (ADR 0016) — which of the
 * streets heads a ride. `server/src/towns/daylight.ts` describes it, but that file
 * carries the arithmetic too, which the web build has no business importing; the
 * shape is two fields, and `tool/contract.mjs` checks them against a live server.
 */
export interface DaylightView {
  phase: 'DAY' | 'NIGHT';
  until: string;
}

export const getDaylight = (api: Api, townId: string) =>
  api.get<DaylightView>(`/api/towns/${townId}/daylight`);

/**
 * «Подтвердить номер» — the number to prove; a gateway texts a code to it (ADR
 * 0052). Nothing is created until the code is typed and [register] spends the proof.
 */
export const startProof = (api: Api, phone: string, purpose: 'REGISTER' | 'RESTORE' = 'REGISTER') =>
  api.post<StartedProofView>('/api/auth/proof', { phone, purpose });

/**
 * Where a proof stands — «Новый номер»'s too. The server holds the answer up to
 * twenty-five seconds while it still says [known].
 */
export const pollProof = (api: Api, id: string, known?: string) =>
  api.get<ProofStateView>(
    `/api/auth/proof/${id}?wait=25${known ? `&known=${encodeURIComponent(known)}` : ''}`,
  );

/** The code from the SMS. Right, and the answer carries the proof to spend. */
export const checkProof = (api: Api, id: string, code: string) =>
  api.post<ProofStateView>(`/api/auth/proof/${id}/code`, { code });

/** «Отправить код ещё раз»: a new code, and the old one stops working. */
export const resendProof = (api: Api, id: string) =>
  api.post<StartedProofView>(`/api/auth/proof/${id}/resend`);

/** The proved number, a name, and the password every later sign-in uses. */
export const register = (
  api: Api,
  body: {
    verificationToken: string;
    firstName: string;
    lastName?: string;
    password: string;
    /**
     * «Даю согласие на обработку персональных данных», which the button waits for;
     * none while the box is not shown (`legalDocumentsLive`).
     */
    consent?: boolean;
  },
) => api.post<SessionView>('/api/auth/passenger/register', body);

/**
 * «Согласие», ticked — sent when it was ticked, which may be a while ago (ADR 0030).
 */
export const sendConsent = (api: Api, consent: { edition: string; at: string }) =>
  api.post<unknown>('/api/me/consent', {
    edition: consent.edition,
    at: consent.at,
    sentAt: new Date().toISOString(),
  });

/** «Войти» — no SMS, and no name to re-type: the account already knows both. */
export const signIn = (api: Api, phone: string, password: string) =>
  api.post<SessionView>('/api/auth/passenger/session', { phone, password });

/**
 * «Восстановление доступа» — the number proved again, and the new password (ADR
 * 0022). Signs in here, and out everywhere else the account was.
 */
export const restorePassword = (
  api: Api,
  body: { verificationToken: string; password: string },
) => api.post<SessionView>('/api/auth/password/restore', body);

export const endSession = (api: Api, refreshToken: string) =>
  api.delete<unknown>('/api/auth/session', { refreshToken });

// ------------------------------------------------------------------------ the passenger

/** The inbox. Rows only — the conversations are a request each. */
export const getSupportThreads = (api: Api) =>
  api.get<SupportThreadView[]>('/api/support/threads');

export const getSupportThread = (api: Api, id: string) =>
  api.get<SupportThreadView>(`/api/support/threads/${id}`);

/** One screenshot. Returns the key the message then names. */
export const uploadSupportImage = (api: Api, file: File) =>
  api.upload<{ storageKey: string }>('/api/uploads/support', file);

/** A new question, which comes back as the thread it opened. */
export const askSupport = (api: Api, text: string, attachments: string[]) =>
  api.post<SupportThreadView>('/api/support/threads', { text, attachments });

/** Another message under a thread that already exists. */
export const replyToSupport = (
  api: Api,
  threadId: string,
  text: string,
  attachments: string[],
) =>
  api.post<SupportMessageView>(`/api/support/threads/${threadId}/messages`, {
    text,
    attachments,
  });

/** Opening a thread clears its badge. */
export const markSupportRead = (api: Api, threadId: string) =>
  api.post<{ ok: true }>(`/api/support/threads/${threadId}/read`);

/**
 * «Удалить переписку» — the thread goes, with its messages and its screenshots,
 * and support loses it too. Nothing about it can be got back afterwards.
 */
export const deleteSupportThread = (api: Api, threadId: string) =>
  api.delete<{ ok: true }>(`/api/support/threads/${threadId}`);

/** A screenshot already sent, which needs the session and so cannot be an `<img src>`. */
export const getSupportAttachment = (api: Api, id: string) =>
  api.blob(`/api/support/attachments/${id}`);

export const getMe = (api: Api) => api.get<MeView>('/api/me');

/**
 * A face — the passenger's own, or their driver's — by the key a view handed out.
 * The key is the whole «avatars/…» path the server filed it under; the route fixes
 * the folder, so only the file is sent. Needs the session, so not an `<img src>`.
 */
export const getAvatar = (api: Api, key: string) =>
  api.blob(`/api/avatars/${key.split('/').pop()}`);

/**
 * «Фото профиля» — uploaded, then claimed by the profile. A file that uploaded but
 * was never claimed is swept, so a failure between the two leaves the old face in
 * place rather than a broken one.
 */
export async function setAvatar(api: Api, file: File): Promise<MeView> {
  const { storageKey } = await api.upload<{ storageKey: string }>('/api/uploads/avatar', file);
  return api.patch<MeView>('/api/me', { avatarKey: storageKey });
}

/** «Удалить фото» — the profile lets go of the key, and the server deletes the file. */
export const removeAvatar = (api: Api) => api.patch<MeView>('/api/me', { avatarKey: null });

export const setTown = (api: Api, townId: string) =>
  api.patch<MeView>('/api/me', { townId });

export const rename = (api: Api, firstName: string, lastName: string) =>
  api.patch<MeView>('/api/me', { firstName, lastName });

/** «Новый номер» — proved by the code texted to the number being moved *to*. */
export const startPhoneProof = (api: Api, phone: string) =>
  api.post<StartedProofView>('/api/me/phone/proof', { phone });

/**
 * «Старый перестанет работать для входа». A POST, as the server has it — this was a
 * PATCH once, to a route that never existed, so the web's «Новый номер» could not
 * have worked.
 */
export const changePhone = (api: Api, verificationToken: string) =>
  api.post<{ ok: true }>('/api/me/phone', { verificationToken });

/**
 * «Сменить пароль». Every session is revoked, this one included — the caller signs
 * out afterwards rather than pretending otherwise.
 */
export const changePassword = (api: Api, currentPassword: string, newPassword: string) =>
  api.post<{ ok: true }>('/api/me/password', { currentPassword, newPassword });

/**
 * «Удалить аккаунт» (ADR 0048). The server erases the account and ends every session
 * with it — the caller signs out afterwards, as after a new password.
 */
export const removeAccount = (api: Api, password: string) =>
  api.post<{ ok: true }>('/api/me/remove', { password });

// --------------------------------------------------------------------------- addresses

/** The town catalogue. Free text is never refused, so this is a convenience. */
export const searchPlaces = (api: Api, townId: string, q: string) =>
  api.get<PlaceView[]>(
    `/api/places?townId=${encodeURIComponent(townId)}&q=${encodeURIComponent(q)}`,
  );

export const getSavedPlaces = (api: Api) => api.get<SavedPlaceView[]>('/api/me/places');

export const addSavedPlace = (api: Api, label: string, address: string) =>
  api.post<SavedPlaceView>('/api/me/places', { label, address });

export const updateSavedPlace = (api: Api, id: string, label: string, address: string) =>
  api.put<SavedPlaceView>(`/api/me/places/${id}`, { label, address });

export const removeSavedPlace = (api: Api, id: string) =>
  api.delete<unknown>(`/api/me/places/${id}`);

// ------------------------------------------------------------------------------- rides

/**
 * Placing an order. `id` names it before it is sent (ADR 0030): an order whose
 * answer was lost is sent again under the same id, and the server hands back the
 * ride the first send made rather than a second car.
 */
export const order = (
  api: Api,
  body: {
    id?: string;
    townId: string;
    stops: { kind: 'PICKUP' | 'STOP' | 'DESTINATION'; address: string }[];
    scheduledFor?: string;
    comment?: string;
  },
) => api.post<PassengerRideView>('/api/rides', body);

export const getMyRides = (api: Api, take = 50) =>
  api.get<PassengerRideView[]>(`/api/rides/mine?take=${take}`);

export const getRide = (api: Api, id: string) =>
  api.get<PassengerRideView>(`/api/rides/${id}`);

/**
 * «Заказать на утро» — a «сейчас» order nobody has taken, given up for a предзаказ of
 * the same route at `scheduledFor`, in one request. Answers with the предзаказ.
 */
export const laterRide = (api: Api, id: string, scheduledFor: Date) =>
  api.post<PassengerRideView>(`/api/rides/${id}/later`, {
    scheduledFor: scheduledFor.toISOString(),
  });

export const cancelRide = (api: Api, id: string) =>
  api.post<PassengerRideView>(`/api/rides/${id}/cancel`);

/**
 * «Удалить из истории» — a ride that is over leaves «Мои поездки». It is only hidden
 * from the passenger: the panel keeps it (ADR 0057).
 */
export const hideRide = (api: Api, id: string) =>
  api.post<{ ok: true }>(`/api/rides/${id}/hide`);

/** «Очистить историю» — every ride that is over, as {@link hideRide} takes one. */
export const clearHistory = (api: Api) =>
  api.post<{ hidden: number }>('/api/rides/history/clear');

/**
 * `at` is when the passenger left the rating, which one sent later carries beside
 * when it was sent (ADR 0030): the server takes the gap between the two, never the
 * device's clock.
 */
export const rateRide = (api: Api, id: string, stars: number, comment: string, at?: string) =>
  api.post<{ id: string; rideId: string; stars: number }>(`/api/rides/${id}/rating`, {
    stars,
    ...(comment ? { comment } : {}),
    ...(at ? { at, sentAt: new Date().toISOString() } : {}),
  });

// -------------------------------------------------------------------------------- chat

export const getMessages = (api: Api, rideId: string) =>
  api.get<MessageView[]>(`/api/rides/${rideId}/messages`);

export const sendMessage = (api: Api, rideId: string, text: string) =>
  api.post<MessageView>(`/api/rides/${rideId}/messages`, { text });

/**
 * A photograph or a voice message in the ride's chat: the file first, then the
 * message that names it. A voice message is an `.m4a`, which is how the server tells
 * the two apart.
 */
export async function sendChatMedia(
  api: Api,
  rideId: string,
  text: string,
  file: File,
): Promise<MessageView> {
  const { storageKey } = await api.upload<{ storageKey: string }>('/api/uploads/chat', file);
  return api.post<MessageView>(`/api/rides/${rideId}/messages`, { text, storageKey });
}

/** What a chat message carries, which needs the session and so cannot be a link. */
export const getChatAttachment = (api: Api, rideId: string, messageId: string) =>
  api.blob(`/api/rides/${rideId}/messages/${messageId}/attachment`);

/** The chosen messages — this side's copy only, or the driver's as well. */
export const deleteMessages = (
  api: Api,
  rideId: string,
  messageIds: string[],
  forEveryone: boolean,
) => api.post<unknown>(`/api/rides/${rideId}/messages/delete`, { messageIds, forEveryone });

/** The same, in a conversation with «Поддержка». */
export const deleteSupportMessages = (
  api: Api,
  threadId: string,
  messageIds: string[],
  forEveryone: boolean,
) =>
  api.post<unknown>(`/api/support/threads/${threadId}/messages/delete`, {
    messageIds,
    forEveryone,
  });

export const markRead = (api: Api, rideId: string) =>
  api.post<unknown>(`/api/rides/${rideId}/messages/read`);

// ------------------------------------------------------------------------------- push

/**
 * This browser, to be reached with no tab open (ADR 0027): the subscription the
 * browser made against the server's `pushKey`, as its `toJSON()` writes it.
 */
export const enrolPush = (
  api: Api,
  subscription: { endpoint: string; keys: { p256dh: string; auth: string } },
) => api.post<{ ok: true }>('/api/push/subscription', subscription);
